import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { inspectionService } from '@/services/inspection.service';

interface TakeoverDialogProps {
  inspectionId: string;
  open: boolean;
  onClose: () => void;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function TakeoverDialog({ inspectionId, open, onClose }: TakeoverDialogProps) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => inspectionService.requestTakeover(inspectionId, { reason }),
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      setReason('');
      onClose();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to request takeover')),
  });

  const canSubmit = reason.trim().length >= 10 && !mutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title="Request Takeover">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Request ownership of this inspection. The current owner will be notified and must accept
          before the takeover takes effect. The inspection will continue as normal while your
          request is pending.
        </p>

        <FormField label="Reason for Takeover" htmlFor="takeover-reason" required>
          <Textarea
            id="takeover-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you are requesting to take over this inspection..."
            rows={4}
          />
          <span className="text-xs text-muted-foreground">
            {reason.length} / 1000 · minimum 10 characters
          </span>
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? 'Submitting…' : 'Request Takeover'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
